import { TextStyle, ViewStyle } from 'react-native';
import { tokens } from './tokens';

// Utility functions for working with the theme system

/**
 * Parse value to number, handling string values like "16px" or "8"
 */
export const parseValue = (value: number | string | undefined): number | undefined => {
  if (typeof value === 'string') {
    const num = parseFloat(value.replace('px', ''));
    return isNaN(num) ? undefined : num;
  }
  return value;
};

/**
 * Get spacing value from theme
 */
export const getSpacing = (size: keyof typeof tokens.spacing): number => tokens.spacing[size];

/**
 * Get color value from theme
 */
export const getColor = (colorPath: string): string => {
  const keys = colorPath.split('.');
  let value: any = tokens.colors;

  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      return tokens.colors.neutral.charcoal; // Fallback color
    }
  }

  return value;
};

/**
 * Get typography style for a given variant
 */
export const getTypographyVariant = (variant: string): TextStyle => {
  const textTheme = tokens.typography;

  // Handle size-only variants (e.g., "lg", "md")
  if (textTheme.fontSizes[variant as keyof typeof textTheme.fontSizes]) {
    return {
      fontSize: textTheme.fontSizes[variant as keyof typeof textTheme.fontSizes],
      lineHeight: textTheme.lineHeights[variant as keyof typeof textTheme.lineHeights],
    };
  }

  // Handle variant-size combinations (e.g., "lg-bold", "md-normal")
  const [size, weight] = variant.split('-');

  if (size && weight) {
    const fontSize = textTheme.fontSizes[size as keyof typeof textTheme.fontSizes];
    const lineHeight = textTheme.lineHeights[size as keyof typeof textTheme.lineHeights];
    const fontFamily = weight === 'bold' ? textTheme.fontFamilies.bold : textTheme.fontFamilies.regular;

    if (fontSize && lineHeight && fontFamily) {
      return {
        fontSize,
        lineHeight,
        fontFamily,
      };
    }
  }

  // Fallback to default
  return {
    fontSize: textTheme.fontSizes.md,
    lineHeight: textTheme.lineHeights.md,
    fontFamily: textTheme.fontFamilies.regular,
  };
};

/**
 * Create responsive style based on screen size
 */
export const createResponsiveStyle = (baseStyle: ViewStyle | TextStyle): ViewStyle | TextStyle =>
  // For now, return base style
  // In the future, this can be enhanced with actual responsive logic
  baseStyle;

/**
 * Create shadow style
 */
export const createShadow = (shadowType: keyof typeof tokens.shadows): ViewStyle => tokens.shadows[shadowType];

/**
 * Create border style
 */
export const createBorder = (
  width: keyof typeof tokens.borderWidth = 'thin',
  color: string = tokens.colors.neutral.charcoal,
  radius: keyof typeof tokens.borderRadius = 'none',
): ViewStyle => ({
  borderWidth: tokens.borderWidth[width],
  borderColor: color,
  borderRadius: tokens.borderRadius[radius],
});

/**
 * Create spacing style
 */
export const createSpacing = (
  horizontal?: keyof typeof tokens.spacing,
  vertical?: keyof typeof tokens.spacing,
): ViewStyle => {
  const style: ViewStyle = {};

  if (horizontal) {
    style.paddingHorizontal = tokens.spacing[horizontal];
  }

  if (vertical) {
    style.paddingVertical = tokens.spacing[vertical];
  }

  return style;
};

/**
 * Create margin style
 */
export const createMargin = (
  horizontal?: keyof typeof tokens.spacing,
  vertical?: keyof typeof tokens.spacing,
): ViewStyle => {
  const style: ViewStyle = {};

  if (horizontal) {
    style.marginHorizontal = tokens.spacing[horizontal];
  }

  if (vertical) {
    style.marginVertical = tokens.spacing[vertical];
  }

  return style;
};

/**
 * Get component theme variant
 */
export const getComponentVariant = <T extends keyof typeof tokens>(
  // eslint-disable-next-line no-unused-vars
  _component: T,
  // eslint-disable-next-line no-unused-vars
  _variant: string,
  // eslint-disable-next-line no-unused-vars
  _state?: string,
): any =>
  // This is a placeholder for component-specific theme logic
  // Will be implemented when we create the actual component themes
  ({});

/**
 * Legacy compatibility functions
 */
export const getSvgProps = (focused: boolean) => (focused ? { fill: tokens.colors.primary.carnation } : null);

export const getTabBarComponent = (labelName?: string) => (props: any) => ({
  testID: `tab_${labelName}`,
  ...props,
});
